import { useState } from 'react'
import type { Member } from '@rp/contracts'
import { isApiError } from '../../api/client'
import { Card, Spinner } from '../../design'
import { SESSION_COPY } from '../../session/copy'
import { useReadySession } from '../../session/useSession'
import { SETTINGS_COPY } from './copy'
import { InviteMemberForm } from './InviteMemberForm'
import { MemberList, type MemberAction } from './MemberList'
import { MemberPasswordDialog } from './MemberPasswordDialog'
import { MemberRemoveDialog } from './MemberRemoveDialog'
import { MemberRoleDialog, type RoleDraft } from './MemberRoleDialog'
import { PasswordReveal, type RevealedPassword } from './PasswordReveal'
import {
  useChangeMemberRole,
  useMembers,
  useRemoveMember,
  useResetMemberPassword,
} from './memberQueries'

type Pending = { action: MemberAction; member: Member } | null

const nameOf = (member: Member): string => member.displayName ?? member.email

const useMemberMutations = () => {
  const changeRole = useChangeMemberRole()
  const remove = useRemoveMember()
  const resetPassword = useResetMemberPassword()
  const failure = [changeRole.error, remove.error, resetPassword.error].find(isApiError)
  return { changeRole, remove, resetPassword, error: failure?.message ?? null }
}

const SuccessNotice = ({ mutations }: { mutations: ReturnType<typeof useMemberMutations> }) => {
  const { changeRole, remove } = mutations
  const message =
    (changeRole.isSuccess
      ? SETTINGS_COPY.roleChanged(nameOf(changeRole.variables.member), SESSION_COPY.roleLabels[changeRole.data.role])
      : null) ??
    (remove.isSuccess ? SETTINGS_COPY.removed(remove.variables.email) : null)
  if (message === null) return null
  return (
    <p className="settings-saved text-tone tone-success" role="status">
      {message}
    </p>
  )
}

export const MembersSection = () => {
  const { me, membership } = useReadySession()
  const members = useMembers()
  const mutations = useMemberMutations()
  const [open, setOpen] = useState<Pending>(null)
  const [roleDraft, setRoleDraft] = useState<RoleDraft | null>(null)
  const [revealed, setRevealed] = useState<RevealedPassword | null>(null)
  const close = () => {
    setOpen(null)
    setRoleDraft(null)
  }
  const target = (action: MemberAction) => (open?.action === action ? open.member : null)
  const reset = () => {
    ;[mutations.changeRole, mutations.remove, mutations.resetPassword].forEach((mutation) => mutation.reset())
    setRevealed(null)
  }
  const resetPassword = (member: Member, password: string) =>
    mutations.resetPassword.mutate(
      { member, password },
      {
        onSuccess: () => {
          setRevealed({ email: member.email, password })
          mutations.resetPassword.reset()
        },
        onSettled: close,
      },
    )

  const onAction = (action: MemberAction, member: Member) => {
    reset()
    setOpen({ action, member })
    if (action === 'role') setRoleDraft({ member, role: member.role })
  }

  return (
    <Card as="section" className="settings-card">
      <h2>{SETTINGS_COPY.members}</h2>
      <p className="settings-hint measure">{SETTINGS_COPY.membersHint}</p>
      {mutations.error === null ? null : (
        <p className="settings-error text-tone tone-danger" role="alert">
          {mutations.error}
        </p>
      )}
      <SuccessNotice mutations={mutations} />
      {revealed === null ? null : <PasswordReveal revealed={revealed} onDismiss={() => setRevealed(null)} />}
      {members.data === undefined ? (
        <Spinner label={SETTINGS_COPY.loadingMembers} />
      ) : (
        <MemberList members={members.data} selfId={me.user.id} onAction={onAction} />
      )}
      <InviteMemberForm />
      <MemberRoleDialog
        draft={roleDraft}
        members={members.data ?? []}
        selfId={me.user.id}
        pending={mutations.changeRole.isPending}
        onRoleChange={(role) => setRoleDraft((current) => (current === null ? null : { ...current, role }))}
        onCancel={close}
        onConfirm={(draft) => mutations.changeRole.mutate(draft, { onSettled: close })}
      />
      <MemberRemoveDialog
        member={target('remove')}
        workspace={membership.tenantName}
        selfId={me.user.id}
        pending={mutations.remove.isPending}
        onCancel={close}
        onConfirm={(member) => mutations.remove.mutate(member, { onSettled: close })}
      />
      <MemberPasswordDialog
        member={target('password')}
        pending={mutations.resetPassword.isPending}
        onCancel={close}
        onConfirm={resetPassword}
      />
    </Card>
  )
}
